import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Plus, Pencil, Trash2, GripVertical, Upload, ChevronDown, ChevronUp } from 'lucide-react';

const TreatmentManagement = () => {
  const { token } = useAuth();
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    image: '',
    services: [],
    order: 0
  });
  const [newService, setNewService] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchTreatments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/treatments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTreatments(data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch treatments:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchTreatments();
  }, [fetchTreatments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingTreatment 
      ? `${API_URL}/api/admin/treatments/${editingTreatment.id}`
      : `${API_URL}/api/admin/treatments`;
    
    const method = editingTreatment ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          order: formData.order || treatments.length
        })
      });

      if (response.ok) {
        fetchTreatments();
        closeModal();
      }
    } catch (error) {
      console.error('Failed to save treatment:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Behandlung wirklich löschen?')) return;

    try {
      await fetch(`${API_URL}/api/admin/treatments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTreatments();
    } catch (error) {
      console.error('Failed to delete treatment:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: `${API_URL}${data.url}` }));
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
    }
  };

  const addService = () => {
    if (newService.trim()) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, newService.trim()]
      }));
      setNewService('');
    }
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const openEditModal = (treatment) => {
    setEditingTreatment(treatment);
    setFormData({
      title: treatment.title,
      subtitle: treatment.subtitle,
      description: treatment.description,
      image: treatment.image || '',
      services: treatment.services || [],
      order: treatment.order
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTreatment(null);
    setFormData({ title: '', subtitle: '', description: '', image: '', services: [], order: 0 });
    setNewService('');
  };

  const moveItem = async (index, direction) => {
    const newTreatments = [...treatments];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newTreatments.length) return;

    [newTreatments[index], newTreatments[targetIndex]] = [newTreatments[targetIndex], newTreatments[index]];
    
    const orderUpdate = newTreatments.map((t, i) => ({ id: t.id, order: i }));
    
    try {
      await fetch(`${API_URL}/api/admin/treatments/reorder`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderUpdate)
      });
      fetchTreatments();
    } catch (error) {
      console.error('Failed to reorder:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1B4D3E' }}>Behandlungsfelder</h1>
          <p className="text-gray-500">Behandlungen und Texte verwalten</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          style={{ backgroundColor: '#1B4D3E' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Hinzufügen
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {treatments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Noch keine Behandlungen vorhanden
          </div>
        ) : (
          <div className="divide-y">
            {treatments.map((treatment, index) => (
              <div key={treatment.id} className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveItem(index, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveItem(index, 'down')}
                      disabled={index === treatments.length - 1}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                    {treatment.image ? (
                      <img src={treatment.image} alt={treatment.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                        Kein Bild
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">{treatment.title}</h3>
                    <p className="text-sm text-gray-500 truncate">{treatment.subtitle}</p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setExpandedId(expandedId === treatment.id ? null : treatment.id)}
                      className="p-2 hover:bg-gray-200 rounded-lg"
                    >
                      {expandedId === treatment.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => openEditModal(treatment)}
                      className="p-2 hover:bg-gray-200 rounded-lg"
                    >
                      <Pencil className="w-4 h-4" style={{ color: '#1B4D3E' }} />
                    </button>
                    <button
                      onClick={() => handleDelete(treatment.id)}
                      className="p-2 hover:bg-red-100 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                {expandedId === treatment.id && (
                  <div className="mt-4 pl-16 text-sm text-gray-600">
                    <p className="mb-2">{treatment.description}</p>
                    {treatment.services && treatment.services.length > 0 && (
                      <ul className="list-disc pl-4">
                        {treatment.services.map((service, i) => (
                          <li key={i}>{service}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#1B4D3E' }}>
              {editingTreatment ? 'Behandlung bearbeiten' : 'Neue Behandlung'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Titel</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Untertitel</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows={4}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Bild</label>
                <div className="flex items-center gap-4">
                  {formData.image && (
                    <img src={formData.image} alt="Preview" className="w-20 h-20 rounded-lg object-cover" />
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <Upload className="w-4 h-4" />
                    Bild hochladen
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg mt-2"
                  placeholder="Oder Bild-URL eingeben"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Leistungen</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    className="flex-1 px-4 py-2 border rounded-lg"
                    placeholder="Neue Leistung eingeben"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                  />
                  <Button type="button" onClick={addService} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <ul className="space-y-2">
                  {formData.services.map((service, index) => (
                    <li key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded">
                      <span className="flex-1">{service}</span>
                      <button
                        type="button"
                        onClick={() => removeService(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={closeModal} className="flex-1">
                  Abbrechen
                </Button>
                <Button type="submit" className="flex-1" style={{ backgroundColor: '#1B4D3E' }}>
                  Speichern
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentManagement;
